import { useState } from 'react';
import { Bell, Gift, MapPin, Star, Check, Trash2, CheckCheck } from 'lucide-react';
import { useNotifications } from '../hooks/useData';
import { useAuth } from '../contexts/AuthContext';
import { SkeletonList } from '../components/Skeleton';
import './Notifications.css';

interface Notification {
    id: string;
    type: string;
    title: string;
    message: string;
    read: boolean;
    createdAt: string;
}

const Notifications = () => {
    const { user } = useAuth();
    const userId = user?.id;
    const [activeFilter, setActiveFilter] = useState<'all' | 'unread'>('all');

    const {
        data: notifications,
        loading,
        markAsRead,
        markAllAsRead,
        deleteNotification
    } = useNotifications(userId);

    const getIcon = (type: string) => {
        switch (type) {
            case 'promo': return <Gift size={22} />;
            case 'nearby': return <MapPin size={22} />;
            case 'reward': return <Star size={22} />;
            default: return <Bell size={22} />;
        }
    };

    const getIconBg = (type: string) => {
        switch (type) {
            case 'promo': return 'icon-promo';
            case 'nearby': return 'icon-nearby';
            case 'reward': return 'icon-reward';
            default: return 'icon-system';
        }
    };

    const formatTime = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);

        if (diffMins < 1) return 'agora';
        if (diffMins < 60) return `${diffMins}min`;
        if (diffHours < 24) return `${diffHours}h`;
        return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
    };

    const isToday = (dateString: string) => {
        const date = new Date(dateString);
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const unreadCount = notifications.filter((n: Notification) => !n.read).length;
    const displayedNotifications = activeFilter === 'unread'
        ? notifications.filter((n: Notification) => !n.read)
        : notifications;
    const todayNotifications = displayedNotifications.filter((n: Notification) => isToday(n.createdAt));
    const earlierNotifications = displayedNotifications.filter((n: Notification) => !isToday(n.createdAt));

    if (loading) {
        return (
            <div className="notifications-page">
                <div className="notifications-header">
                    <h1>Notificações</h1>
                </div>
                <SkeletonList count={4} />
            </div>
        );
    }

    const renderNotification = (notification: Notification) => (
        <div
            key={notification.id}
            className={`notification-item ${!notification.read ? 'unread' : ''}`}
        >
            <div className={`notification-icon ${getIconBg(notification.type)}`}>
                {getIcon(notification.type)}
            </div>
            <div className="notification-content">
                <div className="notification-header">
                    <h4>{notification.title}</h4>
                    <span className="notification-time">{formatTime(notification.createdAt)}</span>
                </div>
                <p>{notification.message}</p>
            </div>
            <div className="notification-actions">
                {!notification.read && (
                    <button
                        className="action-btn read-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                        }}
                        title="Marcar como lida"
                    >
                        <Check size={18} />
                    </button>
                )}
                <button
                    className="action-btn delete-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                    }}
                    title="Excluir"
                >
                    <Trash2 size={18} />
                </button>
            </div>
        </div>
    );

    return (
        <div className="notifications-page">
            <div className="notifications-header">
                <h1>Notificações</h1>
                {unreadCount > 0 && (
                    <span className="unread-badge">{unreadCount}</span>
                )}
            </div>

            <div className="notifications-actions">
                <div className="filter-tabs">
                    <button className={`tab ${activeFilter === 'all' ? 'active' : ''}`} onClick={() => setActiveFilter('all')}>Todas</button>
                    <button className={`tab ${activeFilter === 'unread' ? 'active' : ''}`} onClick={() => setActiveFilter('unread')}>Não lidas</button>
                </div>
                {unreadCount > 0 && userId && (
                    <button className="mark-all-btn" onClick={() => markAllAsRead(userId)}>
                        <CheckCheck size={18} />
                        Limpar
                    </button>
                )}
            </div>

            {notifications.length > 0 ? (
                <>
                    {todayNotifications.length > 0 && (
                        <div className="time-section">
                            <div className="section-title">Hoje</div>
                            <div className="notifications-list">
                                {todayNotifications.map(renderNotification)}
                            </div>
                        </div>
                    )}

                    {earlierNotifications.length > 0 && (
                        <div className="time-section">
                            <div className="section-title">Anteriores</div>
                            <div className="notifications-list">
                                {earlierNotifications.map(renderNotification)}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="empty-state">
                    <Bell size={56} className="empty-icon" />
                    <h3>Sem notificações</h3>
                    <p>Você está em dia! Novas notificações de ofertas e benefícios aparecerão aqui.</p>
                </div>
            )}
        </div>
    );
};

export default Notifications;
